import type { DomainStory } from "@/components/DomainStory";
import reconDroneCanvas from "@/assets/captures/recon-drone-canvas.jpg";
import reconWide from "@/assets/captures/recon-wide.jpg";
import reconInProcess from "@/assets/captures/recon-inprocess.jpg";

export const HERO = {
  eyebrow: "Djinious Workshop · Platform",
  title: "Functional models. Physical 3D simulation. One loop.",
  lede: "Djinious Workshop runs your device as a system of functional models — motors, ESCs, battery, flight controller — and closes the loop through NVIDIA Isaac Sim rigid-body physics on the GPU. Below: an actual takeoff recorded from that integrated loop, replayed in your browser.",
  chips: ["Live co-simulation", "Isaac Sim 6.0.1 · PhysX 6-DOF", "DGX Spark · GB10 GPU"],
} as const;

export const GAP = {
  eyebrow: "The gap",
  title: "Design tools stop at the drawing. Physical truth starts after you build.",
  body: "Requirements live in documents, components in spreadsheets, simulation in a separate silo, and real dynamics only appear on the bench. Every handoff loses fidelity — and no one can point to where the design actually met real physics. Djinious closes that loop inside one tool.",
} as const;

export const PIPELINE_TEASER = [
  { num: "01", title: "Intent", body: "AI-guided notebooks turn plain language into structured, reviewable requirements." },
  { num: "02", title: "Library", body: "Real parts with full records — spec, interfaces, cost, compliance — and a functional model." },
  { num: "03", title: "System", body: "Wire the parts into a co-simulation: every block a real model exchanging real signals." },
  { num: "04", title: "Digital twin", body: "Isaac Sim closes the loop: model outputs drive 6-DOF GPU physics; pose and IMU stream back." },
  { num: "05", title: "Handoff", body: "BOM, interfaces, costs and manufacturing data ship as a build-ready package." },
] as const;

// TASK-1 STUB: wired up so CtaBand.tsx (ported from LAB) compiles. Text
// matches the copy already specified for Task 2 Step 3 ("lab-composition
// Home"), so Task 2 should be able to keep this as-is — extend/refine there.
export const CLOSING_CTA = {
  title: "Watch a design meet real physics.",
  body: "Bring a real part list — we'll wire it, close the loop through Isaac Sim, and show you the physics.",
  cta: { label: "Book a demo", to: "/book-demo" },
} as const;

// Home page "How it works" — the 5-stage PIPELINE_TEASER condensed into the
// three stages the Home composition has room for (Platform.tsx keeps the
// full five-stage walkthrough via STAGES in content/platform.ts).
export const HOW_IT_WORKS = [
  {
    num: "01",
    title: "Intent",
    body: "AI-guided notebooks turn plain language into structured, reviewable requirements and a functional breakdown.",
  },
  {
    num: "02",
    title: "Library & system",
    body: "Real parts with full records — spec, interfaces, cost, compliance, a functional model — wired into a live co-simulation on the canvas.",
  },
  {
    num: "03",
    title: "Twin & handoff",
    body: "Isaac Sim closes the loop with 6-DOF GPU physics; the same model that flew in simulation ships as a build-ready package.",
  },
] as const;

// Home page domain-story band — three beats through the one loop (compose →
// physics-in-the-loop → validate), each illustrated with a real capture.
// DomainStory's beat labels (Model / Simulate / Generate) are the shared
// component's fixed vocabulary; the body text below fits Workshop's actual
// loop to that vocabulary rather than inventing new labels.
export const DOMAIN_STORIES: DomainStory[] = [
  {
    image: reconDroneCanvas,
    imageAlt:
      "System canvas wiring a drone's radio, flight controller, ESCs, motors, propellers, battery and airframe into one co-simulation.",
    domain: "Compose",
    system: "A drone becomes a system, one wire at a time.",
    blurb:
      "Radio, flight controller, ESCs, motors, propellers, battery and airframe — each block a real functional model, wired into one co-simulation on the canvas.",
    beats: {
      model: "Every block is a real component record with a functional model attached.",
      simulate: "Wire it and the co-simulation runs live — real signals, not stand-ins.",
      generate: "The system you wired on the canvas is the system that reaches the twin.",
    },
    tone: "primary",
  },
  {
    image: reconWide,
    imageAlt:
      "Workshop and system view during a live run, airframe driven by NVIDIA Isaac Sim rigid-body physics.",
    domain: "Physics-in-the-loop",
    system: "Isaac Sim closes the loop on the GPU.",
    blurb:
      "The airframe block hands off to NVIDIA Isaac Sim: per-rotor thrust and torque from the functional models drive a 6-DOF PhysX rigid body, and pose, IMU and GPS stream straight back into the running system.",
    beats: {
      model: "Functional models compute thrust, torque and control at every step.",
      simulate: "Isaac Sim's PhysX engine resolves real rigid-body dynamics on the GPU.",
      generate: "Pose, IMU and GPS stream back — the twin and the design, in lockstep.",
    },
    tone: "accent",
  },
  {
    image: reconInProcess,
    imageAlt:
      "Live scopes in the Workshop reading airframe position and rate signals streamed back from NVIDIA Isaac Sim during a running co-simulation.",
    domain: "Validate",
    system: "Watch the physics agree with the math.",
    blurb:
      "Live scopes read airframe position and rate signals streamed back from Isaac Sim — the same signals a gated GPU test suite checks against a deterministic analytic model on every merge.",
    beats: {
      model: "A deterministic 6-DOF analytic model gives the expected answer.",
      simulate: "Isaac Sim's GPU physics runs the same manoeuvre for real.",
      generate: "4/4 gated checks — reachability, hover, agreement — every merge.",
    },
    tone: "neutral",
  },
];

// Home page stat strip — the same four capability proof points from
// `content/proof.ts`, split into a headline value + label for the grid.
export const STATS = [
  { value: "6.0.1", label: "Isaac Sim · PhysX 6-DOF" },
  { value: "GB10", label: "NVIDIA DGX Spark" },
  { value: "4/4", label: "gated GPU validation" },
  { value: "< 0.5 m", label: "Analytic ⇄ Isaac Δ" },
] as const;
